import { CardContent, CardShell } from '@/components/cards/common';
import NoDataMessage from '@/components/states/NoDataMessage';
import { PercentagesList } from './PercentagesList';
import { themeRawValues } from '@/utils/styleUtils';
import { twMerge } from 'tailwind-merge';
import { useTopDevicesQuery } from '@/services/revenueApi';
import useFetchQuery from '@/hooks/useFetchQuery';
import { PercentageListResponse } from '@/types/api/shows';
import { Button, Icon, Tooltip } from '@acast-tech/decibel';
import { ChevronRightRegularGlyph, CircleInfoLightGlyph } from '@acast-tech/decibel-glyphs';
import { DevicesPercentageListTooltipContent } from './PercentagesList/DevicesPercentageListTooltipContent';
import TopDevicesModal from './TopDevicesModal';
import { useLDClient } from 'launchdarkly-react-client-sdk';
import { AUDIENCE_TAB_EXPERIMENTAL_FEATURES_FLAG } from '@/utils/constants';

const colors = { Devices: themeRawValues.colors.chartColors.color3 };

const TopDevicesCard = ({ className }: { className?: string }) => {
  const { queryResult } = useFetchQuery(useTopDevicesQuery, undefined, { sources: [] });

  return (
    <CardShell className={twMerge(className, 'flex flex-col gap-1')} title="Devices">
      <h3 className="text-textSecondary font-light font-telegraf text-lg mb-4">
        By downloads of your podcast on podcast players{' '}
        <Tooltip
          title={
            <div className="flex flex-col gap-2">
              <p className="font-medium">Top devices only available for your audio podcast</p>
              <p>
                The data presented here only accounts for content consumed on podcast player apps
                such as for instance Spotify, Apple podcast or Castbox.
              </p>
              <p>It does not account for YouTube, Instagram and TikTok.</p>
            </div>
          }
          placement="bottom-end"
        >
          <Icon glyph={CircleInfoLightGlyph} />
        </Tooltip>
      </h3>
      <CardContent
        {...queryResult}
        renderData={(data: PercentageListResponse) => <Data data={data.data} />}
      />
    </CardShell>
  );
};

const Data = ({ data }: { data: PercentageListResponse['data'] }) => {
  const ldClient = useLDClient();
  const audienceTabExperimentalFeaturesEnabled =
    ldClient?.variation(AUDIENCE_TAB_EXPERIMENTAL_FEATURES_FLAG, false) ?? false;

  if (Object.keys(data).length < 1) {
    return <NoDataMessage dataType="data" />;
  }

  return (
    <div className="flex flex-col gap-4 h-full justify-between">
      <PercentagesList data={data} colors={colors} usePercentage={true} renderTooltipContent={DevicesPercentageListTooltipContent}/>
      {audienceTabExperimentalFeaturesEnabled ? (
        <TopDevicesModal />
      ) : (
        <div className="flex flex-row justify-end mt-3">
          <Tooltip title="Coming soon" placement="bottom-end">
            <span>
              <Button
                className="self-right"
                variant="tertiary"
                disabled={true}
                glyphEnd={ChevronRightRegularGlyph}
              >
                More details
              </Button>
            </span>
          </Tooltip>
        </div>
      )}
    </div>
  );
};

export default TopDevicesCard;
